import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import mapa from "./countries.json";

const BASE_MIN = "#2df594";
const BASE_MAX = "#20BC71";

const COLOR_STOPS = [
    "#e9fdf3",
    "#bdfbe0",
    "#7ef5bf",
    BASE_MIN,
    "#26e689",
    "#22d17c",
    BASE_MAX,
    "#1a9e5f",
    "#127549",
];

const COUNTRY_BORDER = "#2e7d32";

const lerpHex = (a, b, t) => {
    const h = (s) => parseInt(s, 16);
    const A = [h(a.slice(1, 3)), h(a.slice(3, 5)), h(a.slice(5, 7))];
    const B = [h(b.slice(1, 3)), h(b.slice(3, 5)), h(b.slice(5, 7))];
    const C = A.map((v, i) =>
        Math.round(v + (B[i] - v) * t)
            .toString(16)
            .padStart(2, "0")
    );
    return `#${C.join("")}`;
};

const ramp = (t) => {
    const x = Math.max(0, Math.min(1, t));
    const n = COLOR_STOPS.length - 1;
    const i = Math.min(n - 1, Math.floor(x * n));
    const localT = (x - i / n) * n;
    return lerpHex(COLOR_STOPS[i], COLOR_STOPS[i + 1], localT);
};

function normalizeIso(value) {
    return typeof value === "string" ? value.trim().toUpperCase() : "";
}

function FitMap({ data }) {
    const map = useMap();

    useEffect(() => {
        if (!data?.features?.length) return;

        const layer = L.geoJSON(data);
        const bounds = layer.getBounds();

        const GAP_RATIO = 0.01;
        const GAP_PX = 24;

        const padded = bounds.pad(GAP_RATIO);

        const fit = () => {
            map.fitBounds(padded, {
                paddingTopLeft: [GAP_PX, GAP_PX],
                paddingBottomRight: [GAP_PX, GAP_PX],
                animate: false,
            });
        };

        fit();

        map.dragging.disable();
        map.scrollWheelZoom.disable();
        map.doubleClickZoom.disable();
        map.boxZoom.disable();
        map.keyboard.disable();
        map.touchZoom.disable();
        map.zoomControl?.remove();

        map.on("resize", fit);

        const t = setTimeout(() => {
            map.invalidateSize();
            fit();
        }, 70);

        return () => {
            map.off("resize", fit);
            clearTimeout(t);
        };
    }, [data, map]);

    return null;
}

export default function MexicoLatAmMap({
    dataKey,
    countriesData = {},
    onSelectCountry,
    selectedId,
    setSelectedId,
    lastUpdated = "28th July 2025",
}) {
    const [mexico, setMexico] = useState(null);
    const [hoverId, setHoverId] = useState(null);

    const geoJsonRef = useRef(null);

    const normalizedCountriesData = useMemo(() => {
        return Object.fromEntries(
            Object.entries(countriesData || {}).map(([key, value]) => [
                normalizeIso(key),
                value,
            ])
        );
    }, [countriesData]);

    useEffect(() => {
        const raw = mapa;

        const LATAM_A3 = new Set([
            "MEX", "GTM", "HND", "SLV", "NIC", "CRI", "PAN", "CUB", "DOM", "HTI",
            "COL", "VEN", "ECU", "PER", "BOL", "BRA", "PRY", "URY", "CHL", "ARG",
            "BHS", "BRB", "TTO", "GUY", "SUR", "BLZ", "JAM",
        ]);

        const featuresAll = Array.isArray(raw.features) ? raw.features : [];
        const normalized = featuresAll.map((f) => {
            const p = f.properties || {};
            const ISO_A3 = normalizeIso(
                p.ISO_A3 || p.ADM0_A3 || p.shapeISO || p.iso_a3 || p.adm0_a3
            );
            const name = p.name || p.ADMIN || p.shapeName || p.sovereignt || p.admin;
            return { ...f, properties: { ...p, ISO_A3, name } };
        });

        const filtered = normalized.filter((f) => LATAM_A3.has(f.properties?.ISO_A3));
        setMexico({ type: "FeatureCollection", features: filtered });

        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute("width", 0);
        svg.setAttribute("height", 0);
        svg.innerHTML = `
      <defs>
        <pattern id="diagonalHatch" patternUnits="userSpaceOnUse" width="5" height="5" patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2="8" stroke="#238b45" stroke-width="2"/>
        </pattern>
      </defs>
    `;
        document.body.appendChild(svg);

        return () => svg.remove();
    }, []);

    const cMax = useMemo(() => {
        const vals = Object.values(normalizedCountriesData).filter(
            (n) => typeof n === "number" && isFinite(n)
        );
        return vals.length ? Math.max(...vals) : 1;
    }, [normalizedCountriesData]);

    const colorForCountry = (v) => {
        if (v == null || !isFinite(v)) return COLOR_STOPS[0];
        if (cMax <= 0) return COLOR_STOPS[0];

        const vv = Math.max(0, v);
        const max = Math.max(1, cMax);
        const t = Math.log(vv + 1) / Math.log(max + 1);

        return ramp(t);
    };

    const countryStyle = (feature) => {
        const iso = normalizeIso(feature.properties?.ISO_A3);
        const v = normalizedCountriesData[iso];
        const hover = hoverId === iso;

        const fill = colorForCountry(v);

        const base = {
            color: hover ? "#1b5e20" : COUNTRY_BORDER,
            weight: 1,
            fillColor: hover ? lerpHex(fill, "#000000", 0.18) : fill,
            fillOpacity: 1,
        };

        if (selectedId && iso !== normalizeIso(selectedId)) {
            return {
                ...base,
                fillColor: `url(#diagonalHatch)`,
                fillOpacity: 0.75,
            };
        }

        return base;
    };

    const onEachCountry = (feature, layer) => {
        const iso = normalizeIso(feature.properties?.ISO_A3);
        const name = feature.properties?.name || iso;
        const v = normalizedCountriesData[iso];

        layer.on({
            mouseover: () => setHoverId(iso),
            mouseout: () => setHoverId(null),
            click: () => {
                const newIso = normalizeIso(selectedId) === iso ? null : iso;
                setSelectedId(newIso);

                if (newIso === null) onSelectCountry?.(null, "All countries");
                else onSelectCountry?.(newIso, name);
            },
        });

        layer.bindTooltip(`<strong>${name}</strong><br/>${v ?? "N/A"} aislados`, {
            sticky: true,
            direction: "top",
            opacity: 0.99,
        });
    };

    useEffect(() => {
        const gj = geoJsonRef.current;
        if (!gj) return;
        if (typeof gj.setStyle === "function") gj.setStyle(countryStyle);
    }, [dataKey, normalizedCountriesData, cMax, selectedId, hoverId]);

    function InvalidateSizeOnResize() {
        const map = useMap();
        useEffect(() => {
            const el = map.getContainer().parentElement || map.getContainer();
            const ro = new ResizeObserver(() => map.invalidateSize());
            ro.observe(el);
            return () => ro.disconnect();
        }, [map]);
        return null;
    }

    return (
        <MapContainer
            center={[-15, -75]}
            zoom={1}
            zoomSnap={0.1}
            zoomDelta={0.1}
            scrollWheelZoom={false}
            style={{ width: "100%", height: "100%", background: "#fff" }}
        >
            <InvalidateSizeOnResize />
            {mexico && <FitMap data={mexico} />}

            {mexico && (
                <GeoJSON
                    ref={geoJsonRef}
                    key={`geo-${dataKey}-${selectedId ?? "all"}-${JSON.stringify(countriesData)}`}
                    data={mexico}
                    style={countryStyle}
                    onEachFeature={onEachCountry}
                />
            )}

            <LegendControl min={0} max={cMax} position="bottomleft" />
            <UpdateTagControl text={lastUpdated} position="topright" />
        </MapContainer>
    );
}

function LegendControl({ min, max, position = "topright" }) {
    const map = useMap();

    const fmt = (n) => {
        if (n == null || !isFinite(n)) return "N/A";
        if (Math.abs(n) >= 1_000_000) return (n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0) + "M";
        if (Math.abs(n) >= 1_000) return (n / 1_000).toFixed(n % 1_000 ? 1 : 0) + "K";
        return String(n);
    };

    useEffect(() => {
        const ctrl = L.control({ position });

        ctrl.onAdd = () => {
            const div = L.DomUtil.create("div", "latam-legend");
            div.style.cssText = `
        padding: 10px 12px; border:1px solid #222; border-radius:12px;
        background:#fff; box-shadow:0 2px 8px rgba(0,0,0,.08);
        font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
        font-size:12px; line-height:1.25; color:#222;
      `;

            const title = document.createElement("div");
            title.textContent = "Isolates total";
            title.style.fontWeight = "600";
            title.style.marginBottom = "8px";
            div.appendChild(title);

            const row = document.createElement("div");
            row.style.display = "grid";
            row.style.gridTemplateColumns = "18px auto";
            row.style.columnGap = "10px";
            row.style.alignItems = "center";
            div.appendChild(row);

            const bar = document.createElement("div");
            bar.style.cssText = `
        width:18px; height:96px; border-radius:4px;
        background: linear-gradient(to bottom, ${COLOR_STOPS.slice().reverse().join(", ")});
        border:1px solid rgba(0,0,0,.2);
      `;
            row.appendChild(bar);

            const labels = document.createElement("div");
            labels.style.display = "flex";
            labels.style.flexDirection = "column";
            labels.style.height = "96px";
            labels.style.justifyContent = "space-between";
            labels.innerHTML = `
        <div style="transform: translateY(-2px)">${fmt(max)}</div>
        <div style="transform: translateY(2px)">${fmt(min)}</div>
      `;
            row.appendChild(labels);

            L.DomEvent.disableClickPropagation(div);
            L.DomEvent.disableScrollPropagation(div);
            return div;
        };

        ctrl.addTo(map);
        return () => ctrl.remove();
    }, [map, min, max, position]);

    return null;
}

function UpdateTagControl({ text = "28th July 2025", position = "topright" }) {
    const map = useMap();

    useEffect(() => {
        const ctrl = L.control({ position });
        ctrl.onAdd = () => {
            const div = L.DomUtil.create("div", "update-tag");
            div.style.cssText = `
        padding: 4px 16px;
        border: 1px solid #333;
        border-radius: 20px;
        background: #fff;
        font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
        font-size: 12px;
        font-weight: 500;
        color: #000;
        box-shadow: 0 1px 4px rgba(0,0,0,.1);
        margin: 10px;
      `;
            div.innerHTML = `<strong>Updated:</strong> ${text}`;
            L.DomEvent.disableClickPropagation(div);
            L.DomEvent.disableScrollPropagation(div);
            return div;
        };

        ctrl.addTo(map);
        return () => ctrl.remove();
    }, [map, text, position]);

    return null;
}